import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useBKOPortabilidadeFilaTrabalho } from '@/hooks/useBKOPortabilidadeFilaTrabalho';
import { cn } from '@/lib/utils';
import {
  Inbox,
  Smartphone,
  MessageSquareHeart,
  ReceiptText,
  Hourglass,
  CheckCircle2,
} from 'lucide-react';

type ChipColor = 'verde' | 'amarelo' | 'vermelho';

function corPorTempo(total: number, horas: number): ChipColor {
  if (total === 0) return 'verde';
  if (horas >= 24) return 'vermelho';
  if (horas >= 12) return 'amarelo';
  return 'verde';
}

/** Cor apenas no ícone — chip em estilo sidebar (ghost). */
function corIcone(cor: ChipColor, vazio: boolean) {
  if (vazio) return 'text-success/80';
  switch (cor) {
    case 'vermelho':
      return 'text-danger';
    case 'amarelo':
      return 'text-warning';
    default:
      return 'text-info';
  }
}

interface ChipProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  total: number;
  horas: number;
  onClick: () => void;
}

const Chip: React.FC<ChipProps> = ({ icon: Icon, label, total, horas, onClick }) => {
  const vazio = total === 0;
  const cor = corPorTempo(total, horas);
  const urgente = cor === 'vermelho' && !vazio;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[11px] font-medium',
        'text-foreground/80 hover:text-foreground',
        'hover:bg-accent/60 transition-colors',
      )}
      title={
        vazio
          ? `${label}: tudo em dia`
          : `${label}: ${total} pendentes — mais antigo há ${Math.round(horas)}h`
      }
    >
      <Icon className={cn('h-3.5 w-3.5', corIcone(cor, vazio), urgente && 'animate-pulse')} />
      <span className="tabular-nums tracking-tight">{vazio ? '0' : total}</span>
      <span className="hidden xl:inline text-muted-foreground">{label}</span>
    </button>
  );
};

/**
 * Versão antiga (linha cheia abaixo do header).
 * Mantida por compatibilidade — não é mais usada no Layout principal.
 */
export function BkoPortabilidadeBanner() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useBKOPortabilidadeFilaTrabalho();

  if (profile?.funcao !== 'BKO_PORTABILIDADE') return null;
  if (isLoading || !data) return null;

  const total =
    data.triagemInspecao.total +
    data.smsAnatel.total +
    data.bv.total +
    data.fatura.total +
    data.conversasEsperando.total;

  const tudoEmDia = total === 0;

  const goConversa = (convId: string | null, fallback: string) => {
    if (convId) navigate(`/whatsapp-inbox?conversation=${convId}`);
    else navigate(fallback);
  };

  return (
    <div
      className={cn(
        'shrink-0 border-b px-4 py-1.5 flex items-center gap-2 flex-wrap',
        tudoEmDia
          ? 'bg-success/5 border-success/20'
          : 'bg-surface border-border',
      )}
    >
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70 mr-1">
        Trabalho:
      </span>

      {tudoEmDia ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-success font-medium">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Tudo em dia — boa!
        </span>
      ) : (
        <>
          <Chip
            icon={Inbox}
            label="Triagem Inspeção"
            total={data.triagemInspecao.total}
            horas={data.triagemInspecao.maisAntigoHoras}
            onClick={() => navigate('/kanban?quadro=PRE_ATIVO&coluna=INSPE%C3%87%C3%83O%20CONCLUIDA')}
          />
          <Chip
            icon={Smartphone}
            label="SMS Anatel"
            total={data.smsAnatel.total}
            horas={data.smsAnatel.maisAntigoHoras}
            onClick={() => navigate('/kanban?quadro=PRE_ATIVO&coluna=SMS%20ENVIADO')}
          />
          <Chip
            icon={MessageSquareHeart}
            label="BV"
            total={data.bv.total}
            horas={data.bv.maisAntigoHoras}
            onClick={() => goConversa(data.bv.proximaConversaId, '/triagens')}
          />
          <Chip
            icon={ReceiptText}
            label="Fatura"
            total={data.fatura.total}
            horas={data.fatura.maisAntigoHoras}
            onClick={() => goConversa(data.fatura.proximaConversaId, '/triagens')}
          />
          <Chip
            icon={Hourglass}
            label="Esperando"
            total={data.conversasEsperando.total}
            horas={data.conversasEsperando.maisAntigoHoras}
            onClick={() => goConversa(data.conversasEsperando.proximaConversaId, '/whatsapp-inbox')}
          />
        </>
      )}
    </div>
  );
}

/**
 * Variante inline — pensada para ficar dentro do header global,
 * ao lado do "Empresarial Prime". Sem wrapper, sem label "TRABALHO:",
 * sem mensagem "Tudo em dia" verbose. Quando não há nada pendente,
 * simplesmente não renderiza chips (header limpo).
 */
export function BkoPortabilidadeChipsInline() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useBKOPortabilidadeFilaTrabalho();

  if (profile?.funcao !== 'BKO_PORTABILIDADE') return null;
  if (isLoading || !data) return null;

  const goConversa = (convId: string | null, fallback: string) => {
    if (convId) navigate(`/whatsapp-inbox?conversation=${convId}`);
    else navigate(fallback);
  };

  const total =
    data.triagemInspecao.total +
    data.smsAnatel.total +
    data.bv.total +
    data.fatura.total +
    data.conversasEsperando.total;

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide">
      {data.triagemInspecao.total > 0 && (
        <Chip
          icon={Inbox}
          label="Triagem"
          total={data.triagemInspecao.total}
          horas={data.triagemInspecao.maisAntigoHoras}
          onClick={() => navigate('/kanban?quadro=PRE_ATIVO&coluna=INSPE%C3%87%C3%83O%20CONCLUIDA')}
        />
      )}
      {data.smsAnatel.total > 0 && (
        <Chip
          icon={Smartphone}
          label="SMS"
          total={data.smsAnatel.total}
          horas={data.smsAnatel.maisAntigoHoras}
          onClick={() => navigate('/kanban?quadro=PRE_ATIVO&coluna=SMS%20ENVIADO')}
        />
      )}
      {data.bv.total > 0 && (
        <Chip
          icon={MessageSquareHeart}
          label="BV"
          total={data.bv.total}
          horas={data.bv.maisAntigoHoras}
          onClick={() => goConversa(data.bv.proximaConversaId, '/triagens')}
        />
      )}
      {data.fatura.total > 0 && (
        <Chip
          icon={ReceiptText}
          label="Fatura"
          total={data.fatura.total}
          horas={data.fatura.maisAntigoHoras}
          onClick={() => goConversa(data.fatura.proximaConversaId, '/triagens')}
        />
      )}
      {data.conversasEsperando.total > 0 && (
        <Chip
          icon={Hourglass}
          label="Esperando"
          total={data.conversasEsperando.total}
          horas={data.conversasEsperando.maisAntigoHoras}
          onClick={() => goConversa(data.conversasEsperando.proximaConversaId, '/whatsapp-inbox')}
        />
      )}
    </div>
  );
}

export default BkoPortabilidadeBanner;